import {
  CommonNotificationType,
  INotificationBaseMessage,
  LocalMapType,
} from "@evennt-tech/api-v2-core";
import {
  IGetLocalizedLocalMapTranslationKeysRes,
  IGetLocalizedLocalMapTranslationsRes,
} from "@evennt-tech/api-v2-external";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { apiV2 } from "api/firebase";
import { getOptimizedSelector } from "store/utils/get-optimized-selector.utils";

type State = {
  items: IGetLocalizedLocalMapTranslationKeysRes[];
  translationsByType: Map<LocalMapType, IGetLocalizedLocalMapTranslationsRes[]>;
  isTranslationsLoading: boolean;
  isKeysLoading: boolean;
};

type Action = {
  fetchKeys: (force?: boolean) => Promise<void>;
  fetchTranslations: (force?: boolean) => Promise<void>;
  autoCatchKeys: () => () => void;
  autoCatchTranslations: () => () => void;
  resetKeys: () => void;
  resetTranslations: () => void;
};

const DEFAULT_STATE: State = {
  items: [],
  translationsByType: new Map(),
  isKeysLoading: false,
  isTranslationsLoading: false,
};

type Store = State & Action;

export const localizedLocalMapStore = create(
  devtools<Store>(
    (set, get) => ({
      ...DEFAULT_STATE,
      fetchKeys: async (force = false) => {
        const { isKeysLoading, items } = get();

        if (!force && (isKeysLoading || items.length)) return;

        set({ isKeysLoading: true });

        try {
          const fetchedKeys = await apiV2.localization.localMap.getKeys();
          set({ items: fetchedKeys, isKeysLoading: false });
        } catch (e) {
          console.error("Error occurred fetching local map keys: ", e);
          set({ isKeysLoading: false });
        }
      },
      fetchTranslations: async (force = false) => {
        const { isTranslationsLoading, translationsByType } = get();
        if (!force && (isTranslationsLoading || translationsByType.size))
          return;

        if (!force) {
          set({ isTranslationsLoading: true });
        }

        try {
          const translations = await apiV2.localization.localMap.getTranslations();
          const newTranslationsByType = translations.reduce<
            Map<LocalMapType, IGetLocalizedLocalMapTranslationsRes[]>
          >((acc, item) => {
            const existing = acc.get(item.type) ?? [];
            existing.push(item);
            acc.set(item.type, existing);
            return acc;
          }, new Map());

          set({
            translationsByType: newTranslationsByType,
            isTranslationsLoading: false,
          });
        } catch (e) {
          console.error("Error occurred fetching local map translations: ", e);
          if (!force) {
            set({ isTranslationsLoading: false });
          }
        }
      },
      autoCatchKeys: () => {
        apiV2.notification.subscribe<INotificationBaseMessage>({
          key: CommonNotificationType.UPDATE_LOCALIZED_LOCAL_MAP_KEYS,
          callback: async () => {
            if (get().items.length) {
              await get().fetchKeys(true);
            }
          },
        });

        return () =>
          apiV2.notification.unsubscribe({
            key: CommonNotificationType.UPDATE_LOCALIZED_LOCAL_MAP_KEYS,
          });
      },
      autoCatchTranslations: () => {
        apiV2.notification.subscribe({
          key: CommonNotificationType.UPDATE_LOCALIZED_LOCAL_MAP_TRANSLATIONS,
          callback: async () => {
            if (get().translationsByType.size) {
              await get().fetchTranslations(true);
            }
          },
        });

        return () => {
          apiV2.notification.unsubscribe({
            key: CommonNotificationType.UPDATE_LOCALIZED_LOCAL_MAP_TRANSLATIONS,
          });
        };
      },
      resetKeys: () => {
        set({ isKeysLoading: false, items: [] });
      },
      resetTranslations: () => {
        set({ isTranslationsLoading: false, translationsByType: new Map() });
      },
    }),
    {
      name: "localized-local-map",
    }
  )
);

export const useLocalizedLocalMapStore = getOptimizedSelector(
  localizedLocalMapStore
);

export const useLocalizedLocalMapActions = () =>
  localizedLocalMapStore(
    (state) =>
      ({
        fetchKeys: state.fetchKeys,
        fetchTranslations: state.fetchTranslations,
        autoCatchKeys: state.autoCatchKeys,
        autoCatchTranslations: state.autoCatchTranslations,
      } as const)
  );
